import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import useTrainingCourse from "../../Hooks/useTrainingCourse";
import DataLoader from "../../Components/DataLoader";
import SubmitLoader from "../../Components/SubmitLoader";
import AlertMessage from "../../Components/AlertMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCalendarAlt,
  faQuestionCircle,
  faPlayCircle,
  faCheckCircle,
  faTimesCircle,
  faImage,
  faVideo,
  faFileAlt,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import "./CompetencyAssessment.css";

const CompetencyAssessment = () => {
  const { competencyAssessmentId } = useParams();
  const navigate = useNavigate();

  const {
    currentAssessment,
    assessmentLoading,
    assessmentError,
    fetchAssessment,
  } = useTrainingCourse();

  useEffect(() => {
    if (competencyAssessmentId) {
      fetchAssessment(parseInt(competencyAssessmentId));
    }
  }, [competencyAssessmentId]);

  const getMediaIcon = (mediaCategory) => {
    switch (mediaCategory?.toLowerCase()) {
      case "image":
        return faImage;
      case "video":
        return faVideo;
      case "link":
        return faFileAlt;
      default:
        return faFileAlt;
    }
  };

  const getMediaUrl = (media) => {
    // For images and videos, use the fileContent (base64) or mediaImage
    // console.log("Media Object:", media);
    if (media.mediaCategory?.toLowerCase() === "image") {
      if (media.fileContent) {
        return `data:image/${media.fileExtension?.replace('.', '') || 'jpeg'};base64,${media.fileContent}`;
      }
    }
    
    if (media.mediaCategory?.toLowerCase() === "video") {
      if (media.fileContent) {
        return `data:video/${media.fileExtension?.replace('.', '') || 'mp4'};base64,${media.fileContent}`;
      }
    }
    
    // For links, return the mediaLink directly
    if (media.mediaCategory?.toLowerCase() === "link" && media.mediaLink) {
      return media.mediaLink;
    }
    
    // Fallback
    return null;
  };

  if (assessmentLoading) {
    return <SubmitLoader />;
  }

  if (assessmentError) {
    return (
      <AlertMessage
        message={assessmentError}
        type="error"
        className="assessment-error"
      />
    );
  }

  if (!currentAssessment) {
    return (
      <div className="assessment-not-found">
        <h2>Assessment Not Found</h2>
        <p>The requested competency assessment could not be found.</p>
        <button onClick={() => navigate(-1)} className="btn-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          Go Back
        </button>
      </div>
    );
  }

  const questions = currentAssessment.dlTrainingCandidateQuestionAnswers || [];
  const competencyMedia = currentAssessment.dlTrainingCompetencyMedias || [];

  return (
    <div className="assessment-container">
      {/* Header */}
      <div className="assessment-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Course
        </button>

        <div className="competency-info">
          <h1>
            <span className="competency-code">
              {currentAssessment.competencyCode}
            </span>
            <span>{currentAssessment.competencyName}</span>
          </h1>
          <p className="competency-description">
            {currentAssessment.competencyDescription}
          </p>

          {/* Additional competency details */}
          <div className="competency-details">
            {currentAssessment.maxCompetencyAttempts && (
              <div className="detail-item">
                <strong>Assessment Attempts:</strong>{" "}
                <span className="current-attempt-highlight">{currentAssessment.competencyAttemptNum || 0}</span> / {currentAssessment.maxCompetencyAttempts}
                <span className="detail-description">
                  {" "}(You have {currentAssessment.maxCompetencyAttempts - (currentAssessment.competencyAttemptNum || 0)} attempt{currentAssessment.maxCompetencyAttempts - (currentAssessment.competencyAttemptNum || 0) !== 1 ? 's' : ''} remaining)
                </span>
              </div>
            )}
            {currentAssessment.maxQuestionAttempts && (
              <div className="detail-item">
                <strong>Question Attempts:</strong>{" "}
                {currentAssessment.maxQuestionAttempts} per question
                <span className="detail-description">
                  {" "}(Each individual question can be attempted up to {currentAssessment.maxQuestionAttempts} time{currentAssessment.maxQuestionAttempts !== 1 ? 's' : ''})
                </span>
              </div>
            )}
            {currentAssessment.competencyMinutesToComplete && (
              <div className="detail-item">
                <strong>Maximum Time:</strong>{" "}
                {currentAssessment.competencyMinutesToComplete} minute{currentAssessment.competencyMinutesToComplete !== 1 ? 's' : ''}
                <span className="detail-description">
                  {" "}(Approximate time needed to complete this assessment)
                </span>
              </div>
            )}
          </div>

          {/* Rich text content */}
          {currentAssessment.competencyRichText && (
            <div
              className="competency-rich-text"
              dangerouslySetInnerHTML={{
                __html: currentAssessment.competencyRichText,
              }}
            ></div>
          )}
        </div>

        <div className="assessment-progress">
          <span className="progress-text">
            {questions.length} Question{questions.length !== 1 ? "s" : ""}{" "}
            Available
          </span>
        </div>
      </div>

      {/* Competency Media - Only show if media exists */}
      {competencyMedia && competencyMedia.length > 0 && (
        <div className="competency-media-section">
          <h3>Reference Materials</h3>
          <div className="media-list">
            {competencyMedia
              .sort((a, b) => (a.mediaListOrder || 0) - (b.mediaListOrder || 0))
              .map((media, index) => {
                const mediaUrl = getMediaUrl(media);
                
                return (
                  <div key={media.mediaId || index} className="media-item">
                    <div className="media-header">
                      <FontAwesomeIcon icon={getMediaIcon(media.mediaCategory)} />
                      <span>{media.mediaTitle || media.mediaName || `Media ${index + 1}`}</span>
                    </div>
                    
                    {/* Display Image */}
                    {media.mediaCategory?.toLowerCase() === "image" && mediaUrl && (
                      <img
                        src={mediaUrl}
                        alt={media.mediaTitle || media.mediaName || "Media image"}
                        className="media-image"
                        onError={(e) => {
                          console.error("Failed to load image:", e);
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    
                    {/* Display Video */}
                    {media.mediaCategory?.toLowerCase() === "video" && mediaUrl && (
                      <video
                        src={mediaUrl}
                        controls
                        className="media-video"
                        onError={(e) => {
                          console.error("Failed to load video:", e);
                          e.target.style.display = "none";
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                    
                    {/* Display Link - if it's a video URL, embed it as video */}
                    {media.mediaCategory?.toLowerCase() === "link" && mediaUrl && (
                    <div className="media-link-container">
                      {/* Check if the link is a video URL (YouTube, Vimeo, or direct video file) */}
                      {(mediaUrl.includes('youtube.com') || mediaUrl.includes('youtu.be') || 
                        mediaUrl.includes('vimeo.com') || 
                        mediaUrl.match(/\.(mp4|webm|ogg|mov|avi)(\?|$)/i)) ? (
                        <div className="media-video-container">
                          {/* YouTube Video */}
                          {(mediaUrl.includes('youtube.com') || mediaUrl.includes('youtu.be')) && (
                            <iframe
                              src={mediaUrl.includes('youtu.be') 
                                ? `https://www.youtube.com/embed/${mediaUrl.split('/').pop().split('?')[0]}`
                                : mediaUrl.replace('watch?v=', 'embed/')}
                              className="media-video-iframe"
                              frameBorder="0"
                              allowFullScreen
                              title={media.mediaTitle || 'Video'}
                            />
                          )}
                          
                          {/* Vimeo Video */}
                          {mediaUrl.includes('vimeo.com') && (
                            <iframe
                              src={mediaUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                              className="media-video-iframe"
                              frameBorder="0"
                              allowFullScreen
                              title={media.mediaTitle || 'Video'}
                            />
                          )}
                          
                          {/* Direct Video File */}
                          {mediaUrl.match(/\.(mp4|webm|ogg|mov|avi)(\?|$)/i) && (
                            <video
                              src={mediaUrl}
                              controls
                              className="media-video"
                              onError={(e) => {
                                console.error("Failed to load video:", e);
                                e.target.style.display = "none";
                              }}
                            >
                              Your browser does not support the video tag.
                            </video>
                          )}
                        </div>
                      ) : (
                        /* Regular Link */
                        <a
                          href={mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="media-link"
                        >
                          <FontAwesomeIcon icon={faFileAlt} />
                          Open Link
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Media Description - can be used for any type */}
                  {media.mediaDescription && (
                    <p className="media-description">{media.mediaDescription}</p>
                  )}
                  
                  {/* Debug info - remove in production */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="media-debug">
                      <small>DEBUG: Category: {media.mediaCategory} | Size: {media.fileLength ? `${Math.round(media.fileLength / 1024)}KB` : 'N/A'}</small>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Start Quiz Section */}
      <div className="start-quiz-section">
        <div className="quiz-info">
          <h3>Ready to Start Assessment?</h3>
          <p>
            Review the materials above, then click the button below to begin
            your competency assessment.
          </p>

          <div className="quiz-details">
            <div className="detail-item">
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span>{questions.length} Questions Available</span>
            </div>
            {currentAssessment.maxCompetencyAttempts && (
              <div className="detail-item">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>
                  Assessment Attempts: <span className="current-attempt-highlight">{currentAssessment.competencyAttemptNum || 0}</span> / {currentAssessment.maxCompetencyAttempts}
                </span>
              </div>
            )}
            {currentAssessment.competencyMinutesToComplete && (
              <div className="detail-item">
                <FontAwesomeIcon icon={faClock} />
                <span>
                  Maximum Time: {currentAssessment.competencyMinutesToComplete} minute{currentAssessment.competencyMinutesToComplete !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
        <div>
          <button
            onClick={() =>
              navigate(`/trainingcourses/quiz/${competencyAssessmentId}`)
            }
            className="btn-start-quiz"
            disabled={questions.length === 0}
          >
            <FontAwesomeIcon icon={faPlayCircle} />
            Start Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetencyAssessment;
